import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Building2, CalendarClock, CheckSquare, FileUp, StickyNote, UserPlus } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CandidateForm from "./CandidateForm";
import JobForm from "./JobForm";
import ClientForm from "./ClientForm";
import ResumeUpload from "./ResumeUpload";
import { InterviewForm, TaskForm } from "./OpsForms";

const ACTIONS = [
  { key: "candidate", label: "Add Candidate", desc: "Create a profile manually", icon: UserPlus },
  { key: "resume", label: "Upload Resume", desc: "Parse a PDF or DOCX into a profile", icon: FileUp },
  { key: "job", label: "Create Job", desc: "Open a new requirement", icon: Briefcase },
  { key: "client", label: "Add Client", desc: "Register a hiring account", icon: Building2 },
  { key: "interview", label: "Schedule Interview", desc: "Book a panel round", icon: CalendarClock },
  { key: "task", label: "Create Task", desc: "Assign a follow-up", icon: CheckSquare },
  { key: "note", label: "Add Note", desc: "Log a note on a candidate", icon: StickyNote },
];

export function QuickAddDialog({ open, onOpenChange }) {
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const pick = (key) => {
    onOpenChange(false);
    if (key === "note") {
      navigate("/candidates");
      return;
    }
    setActive(key);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-slate-900">Quick add</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                onClick={() => pick(a.key)}
                data-testid={`quick-add-${a.key}`}
                className="surface surface-hover flex items-start gap-3 rounded-md p-3 text-left"
              >
                <span className="mt-0.5 rounded border border-slate-200 bg-slate-100 p-1.5">
                  <a.icon className="h-3.5 w-3.5 text-blue-600" />
                </span>
                <span>
                  <span className="block text-sm text-slate-800">{a.label}</span>
                  <span className="block text-[11px] text-slate-500">{a.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <CandidateForm open={active === "candidate"} onOpenChange={(v) => !v && setActive(null)} onSaved={(c) => navigate(`/candidates/${c.id}`)} />
      <ResumeUpload open={active === "resume"} onOpenChange={(v) => !v && setActive(null)} onSaved={(c) => navigate(`/candidates/${c.id}`)} />
      <JobForm open={active === "job"} onOpenChange={(v) => !v && setActive(null)} onSaved={(j) => navigate(`/jobs/${j.id}`)} />
      <ClientForm open={active === "client"} onOpenChange={(v) => !v && setActive(null)} onSaved={(c) => navigate(`/clients/${c.id}`)} />
      <InterviewForm open={active === "interview"} onOpenChange={(v) => !v && setActive(null)} onSaved={() => navigate("/interviews")} />
      <TaskForm open={active === "task"} onOpenChange={(v) => !v && setActive(null)} onSaved={() => navigate("/tasks")} />
    </>
  );
}
